import { z } from 'zod/v3';

// ============================================================================
// 공통 Enum 스키마
// ============================================================================

/** 쿠폰 상태 */
export const couponStatusSchema = z.enum([
  'NONE',
  'INACTIVE',
  'AWAITING',
  'PROGRESS',
  'ENDED',
]);
export type CouponStatus = z.infer<typeof couponStatusSchema>;

/** 구매자 쿠폰 상태 */
export const buyerCouponStatusSchema = z.enum([
  'NONE',
  'USED',
  'INACTIVE',
  'EXPIRED',
  'AWAITING',
  'USABLE',
  'DOWNLOADABLE',
  'ENDED',
]);
export type BuyerCouponStatus = z.infer<typeof buyerCouponStatusSchema>;

/** 쿠폰 적용 대상 */
export const discountTargetTypeSchema = z.enum([
  'NONE',
  'ALL',
  'VENDOR',
  'PRODUCT',
  'CATEGORY',
  'VENDOR_LIST',
]);
export type DiscountTargetType = z.infer<typeof discountTargetTypeSchema>;

/** 쿠폰 할인 타입 */
export const discountTypeSchema = z.enum(['NONE', 'FIXED', 'PERCENT']);
export type DiscountType = z.infer<typeof discountTypeSchema>;

/** 쿠폰 발급 규칙 */
export const issueRuleSchema = z.enum([
  'NONE',
  'NEW',
  'POINT',
  'TALK_FIRST_CONTENT_REWARD',
  'CASHNOTE_PLUS',
  'CASHNOTE_PLUS_LITE',
  'CASHNOTE_PLUS_GOLD',
  'CASHNOTE_PLUS_12_MONTHS',
  'CASHNOTE_PLUS_12_MONTHS_PROMOTION',
  'CASHNOTE_PLUS_TRIAL_PROMOTION',
  'CASHNOTE_PLUS_100_DEAL_EVENT',
  'CASHNOTE_PLUS_NERGET',
  'CASHNOTE_PLUS_FRANCHISE',
  'CASHNOTE_PLUS_STANDARD_FIRST_HALF',
  'CASHNOTE_PLUS_STANDARD_SECOND_HALF',
  'CASHNOTE_PLUS_STANDARD_PROMOTION',
  'CASHNOTE_PLUS_STANDARD_CONVERSION',
  'CASHNOTE_PLUS_STANDARD_TRIAL_PROMOTION',
  'CASHNOTE_PLUS_RICTAX',
  'CASHNOTE_PLUS_STANDARD_ANNUAL',
  'CASHNOTE_PLUS_STANDARD_ANNUAL_PROMOTION',
  'CASHNOTE_PLUS_STANDARD_3MONTHS',
  'CASHNOTE_PLUS_STANDARD_3MONTHS_PROMOTION',
  'CASHNOTE_PLUS_STANDARD_PRIOR',
  'CASHNOTE_PLUS_STANDARD_RICTAX',
  'CASHNOTE_PLUS_STANDARD_BZNAV',
  'PAY_LATER',
]);
export type IssueRule = z.infer<typeof issueRuleSchema>;

// ============================================================================
// 공통 스키마
// ============================================================================

/**
 * 입점사 정보 스키마
 */
export const couponVendorSchema = z.object({
  /** 입점사 ID */
  vendorId: z.number().optional(),
  /** 입점사 로고 이미지 */
  vendorLogoImage: z.string().optional(),
  /** 입점사 몰 명 */
  vendorMallName: z.string().optional(),
  /** 입점사 명 */
  vendorName: z.string().optional(),
});
export type CouponVendor = z.infer<typeof couponVendorSchema>;

// ============================================================================
// 구매자 쿠폰 스키마
// ============================================================================

/**
 * 구매자 쿠폰 아이템 스키마
 * 쿠폰 목록에서 사용되는 데이터
 */
export const buyerCouponSchema = z.object({
  /** 상호명 */
  businessName: z.string().optional(),
  /** 쿠폰 상세 페이지 URL */
  buyerCouponDetailUrl: z.string().optional(),
  /** 구매자 쿠폰 ID */
  buyerCouponId: z.number().optional(),
  /** 쿠폰 ID */
  couponId: z.number().optional(),
  /** 쿠폰 상태 */
  couponStatus: couponStatusSchema.optional(),
  /** 할인금액 (FIXED 타입) */
  discountAmount: z.number().optional(),
  /** 할인률 (PERCENT 타입) */
  discountRatio: z.number().optional(),
  /** 쿠폰 적용 카테고리 ID 목록 */
  discountTargetCategoryIds: z.array(z.number()).optional(),
  /** 쿠폰 적용 상품 ID 목록 */
  discountTargetOfferIds: z.array(z.number()).optional(),
  /** 쿠폰 적용 대상 */
  discountTargetType: discountTargetTypeSchema.optional(),
  /** 쿠폰 적용 입점사 ID */
  discountTargetVendorId: z.number().optional(),
  /** 쿠폰 할인 타입 */
  discountType: discountTypeSchema.optional(),
  /** 만료기간 */
  expirationAt: z.string().optional(),
  /** 만료기간 설정 여부 (deprecated) */
  expirationDurationSet: z.boolean().optional(),
  /** 쿠폰 유효기간 설정 여부 */
  isExpirationDurationSet: z.boolean().optional(),
  /** 플러스 멤버십 쿠폰 여부 */
  isPlusMembershipCoupon: z.boolean().optional(),
  /** 재사용 여부 */
  isReused: z.boolean().optional(),
  /** 사용 여부 */
  isUsed: z.boolean().optional(),
  /** 발급 규칙 */
  issueRule: issueRuleSchema.optional(),
  /** 최대할인금액 (PERCENT 타입) */
  maximumDiscountAmount: z.number().optional(),
  /** 최소주문금액 */
  minimumOrderAmount: z.number().optional(),
  /** 쿠폰명 */
  name: z.string().optional(),
  /** 유의사항 */
  notice: z.array(z.string()).optional(),
  /** 플러스 멤버십 쿠폰 여부 (deprecated) */
  plusMembershipCoupon: z.boolean().optional(),
  /** 재사용 여부 (deprecated) */
  reused: z.boolean().optional(),
  /** 구매자 쿠폰 상태 */
  status: buyerCouponStatusSchema.optional(),
  /** 입점사 정보 */
  targetVendor: couponVendorSchema.optional(),
  /** 사용 여부 (deprecated) */
  used: z.boolean().optional(),
  /** 사용된 날짜 */
  usedAt: z.string().optional(),
});
export type BuyerCoupon = z.infer<typeof buyerCouponSchema>;

/**
 * 구매자 쿠폰 상세 정보 스키마
 */
export const buyerCouponDetailSchema = z.object({
  /** 구매자 쿠폰 ID */
  buyerCouponId: z.number().optional(),
  /** 쿠폰 ID */
  couponId: z.number().optional(),
  /** 쿠폰명 */
  name: z.string().optional(),
});
export type BuyerCouponDetail = z.infer<typeof buyerCouponDetailSchema>;

// ============================================================================
// 주문 예정 쿠폰 스키마
// ============================================================================

/**
 * 주문 예정 구매자 쿠폰 스키마
 */
export const checkoutBuyerCouponSchema = z.object({
  /** 상호명 */
  businessName: z.string().optional(),
  /** 구매자 쿠폰 ID */
  buyerCouponId: z.number().optional(),
  /** 계산된 할인 금액 */
  calculatedDiscountAmount: z.number().optional(),
  /** 할인금액 (FIXED 타입) */
  discountAmount: z.number().optional(),
  /** 할인률 (PERCENT 타입) */
  discountRatio: z.number().optional(),
  /** 쿠폰 적용 대상 */
  discountTargetType: discountTargetTypeSchema.optional(),
  /** 쿠폰 할인 타입 */
  discountType: discountTypeSchema.optional(),
  /** 만료기간 */
  expirationAt: z.string().optional(),
  /** 만료기간 설정 여부 (deprecated) */
  expirationDurationSet: z.boolean().optional(),
  /** 쿠폰 유효기간 설정 여부 */
  isExpirationDurationSet: z.boolean().optional(),
  /** 중복 여부 */
  isReused: z.boolean().optional(),
  /** 선택 여부 (최적 쿠폰 해당) */
  isSelected: z.boolean().optional(),
  /** 쿠폰 사용 가능 여부 */
  isUsable: z.boolean().optional(),
  /** 발급 규칙 */
  issueRule: issueRuleSchema.optional(),
  /** 최대할인금액 (PERCENT 타입) */
  maximumDiscountAmount: z.number().optional(),
  /** 최소주문금액 */
  minimumOrderAmount: z.number().optional(),
  /** 쿠폰명 */
  name: z.string().optional(),
  /** 유의사항 */
  notice: z.array(z.string()).optional(),
  /** 재사용 여부 (deprecated) */
  reused: z.boolean().optional(),
  /** 선택 여부 (deprecated) */
  selected: z.boolean().optional(),
  /** 구매자 쿠폰 상태 */
  status: buyerCouponStatusSchema.optional(),
  /** 입점사 정보 */
  targetVendor: couponVendorSchema.optional(),
  /** 사용 가능 여부 (deprecated) */
  usable: z.boolean().optional(),
  /** 사용된 날짜 */
  usedAt: z.string().optional(),
});
export type CheckoutBuyerCoupon = z.infer<typeof checkoutBuyerCouponSchema>;

/**
 * 주문 예정 쿠폰 응답 스키마
 */
export const checkoutCouponSchema = z.object({
  /** 적용할 구매자 쿠폰 ID 목록 */
  buyerCouponIds: z.array(z.number()).optional(),
  /** 주문 예정 사용 가능 구매자 쿠폰 목록 */
  checkoutBuyerCoupons: z.array(checkoutBuyerCouponSchema),
  /** 주문 예정 사용 가능 구매자 쿠폰 개수 */
  checkoutUsableBuyerCouponCount: z.number(),
  /** 주문 예정 쿠폰 적용 여부 */
  isUsedBuyerCoupon: z.boolean(),
  /** 캐시노트 플러스 가입권유 노출 여부 */
  showCashnotePlusSuggestion: z.boolean(),
});
export type CheckoutCoupon = z.infer<typeof checkoutCouponSchema>;

// ============================================================================
// 다운로드 쿠폰 스키마
// ============================================================================

/**
 * 다운로드 쿠폰 상품 스키마 (간략)
 */
export const downloadCouponProductSchema = z.object({
  /** 상품 ID */
  offerId: z.number().optional(),
  /** 상품명 */
  name: z.string().optional(),
  /** 썸네일 이미지 URL */
  thumbnailImageUrl: z.string().optional(),
});
export type DownloadCouponProduct = z.infer<typeof downloadCouponProductSchema>;

/**
 * 다운로드 쿠폰 스키마
 */
export const downloadedCouponSchema = z.object({
  /** 상호명 */
  businessName: z.string().optional(),
  /** 쿠폰 상태 */
  couponStatus: couponStatusSchema.optional(),
  /** 할인금액 (FIXED 타입) */
  discountAmount: z.number().optional(),
  /** 할인률 (PERCENT 타입) */
  discountRatio: z.number().optional(),
  /** 쿠폰 적용 대상 */
  discountTargetType: discountTargetTypeSchema.optional(),
  /** 쿠폰 할인 타입 */
  discountType: discountTypeSchema.optional(),
  /** 만료기간 */
  expirationAt: z.string().optional(),
  /** 만료기간 설정 여부 (deprecated) */
  expirationDurationSet: z.boolean().optional(),
  /** 쿠폰 유효기간 */
  expirationPeriod: z.number().optional(),
  /** 쿠폰 ID */
  id: z.number().optional(),
  /** 쿠폰 유효기간 설정 여부 */
  isExpirationDurationSet: z.boolean().optional(),
  /** 발급 여부 */
  isIssued: z.boolean().optional(),
  /** 쿠폰 진행 기간 여부 */
  isProgressDurationSet: z.boolean().optional(),
  /** 쿠폰 타겟 유저 여부 */
  isTargetUser: z.boolean().optional(),
  /** 발행 개수 (0이면 무제한) */
  maxIssueCount: z.number().optional(),
  /** 최대할인금액 (PERCENT 타입) */
  maximumDiscountAmount: z.number().optional(),
  /** 최소주문금액 */
  minimumOrderAmount: z.number().optional(),
  /** 쿠폰명 */
  name: z.string().optional(),
  /** 유의사항 */
  notice: z.array(z.string()).optional(),
  /** 쿠폰 적용 가능한 상품 (최대 4개) */
  products: z.array(downloadCouponProductSchema).optional(),
  /** 진행 기간 설정 여부 (deprecated) */
  progressDurationSet: z.boolean().optional(),
  /** 쿠폰 끝 기간 */
  progressEndAt: z.string().optional(),
  /** 쿠폰 시작 기간 */
  progressStartAt: z.string().optional(),
  /** 타겟 유저 여부 (deprecated) */
  targetUser: z.boolean().optional(),
});
export type DownloadedCoupon = z.infer<typeof downloadedCouponSchema>;

// ============================================================================
// 포인트 쿠폰 스키마
// ============================================================================

/**
 * 포인트 쿠폰 아이템 스키마
 */
export const pointCouponItemSchema = z.object({
  /** 쿠폰 ID */
  couponId: z.number().optional(),
  /** 할인금액 (FIXED 타입) */
  discountAmount: z.number().optional(),
  /** 교환 가능 여부 */
  isExchangeable: z.boolean().optional(),
  /** 최소주문금액 */
  minimumOrderAmount: z.number().optional(),
  /** 쿠폰명 */
  name: z.string().optional(),
  /** 유의사항 */
  notice: z.array(z.string()).optional(),
  /** 포인트 */
  point: z.number().optional(),
  /** 썸네일 이미지 URL */
  thumbnailImageUrl: z.string().optional(),
});
export type PointCouponItem = z.infer<typeof pointCouponItemSchema>;

/**
 * 포인트 쿠폰 목록 응답 스키마
 */
export const pointCouponListSchema = z.array(pointCouponItemSchema);
export type PointCouponList = z.infer<typeof pointCouponListSchema>;

// ============================================================================
// 쿠폰 상세 스키마
// ============================================================================

/**
 * 쿠폰 상세 정보 스키마
 */
export const couponDetailSchema = z.object({
  /** 쿠폰 ID */
  couponId: z.number().optional(),
  /** 쿠폰명 */
  couponName: z.string().optional(),
  /** 만료 일자 */
  expiredAt: z.string().optional(),
  /** 발급 일자 */
  issueAt: z.string().optional(),
  /** 총 발급 수 */
  issueCount: z.number().optional(),
  /** 발급 규칙 */
  issueRule: issueRuleSchema.optional(),
  /** 시작 일자 */
  startAt: z.string().optional(),
  /** 사용된 날짜 */
  usedAt: z.string().optional(),
  /** 사용 수 */
  usedCount: z.number().optional(),
  /** 사용된 주문 금액 */
  usedOrderAmount: z.number().optional(),
});
export type CouponDetail = z.infer<typeof couponDetailSchema>;
